// src/services/api.js
import axios from "axios";
import API_CONFIG from "../config/api.config";
import { supabase } from "../lib/supabaseClient";

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": import.meta.env.VITE_API_KEY,
  },
});

/** The current Supabase access token, or null when signed out. */
export async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

/**
 * Auth headers every backend call carries (X-API-Key + Bearer JWT). Shared with
 * clients that can't go through axios, e.g. the fetch-based SSE client.
 * @param {string | null} [accessToken] override (e.g. a freshly refreshed token)
 */
export async function getAuthHeaders(accessToken) {
  const token = accessToken ?? (await getAccessToken());
  const headers = { "X-API-Key": import.meta.env.VITE_API_KEY };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Session can't be refreshed — sign out and send the user to login. */
export async function handleSessionExpired() {
  await supabase.auth.signOut();
  window.location.href = "/login";
}

// Singleton refresh promise — prevents concurrent refresh calls from racing on the same token.
// If a refresh is already in-flight, all callers await the same promise.
let refreshPromise = null;

/**
 * Refresh the session through the backend /auth/refresh endpoint.
 * This re-validates Tekion and updates tekion_verified_at in the JWT.
 *
 * Returns the new access token on success.
 * Throws on failure (caller is responsible for sign-out/redirect).
 */
export async function ensureFreshSession() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession();

    if (!currentSession?.refresh_token) throw new Error("No refresh token");

    const { data: refreshData } = await axios.post(
      `${API_CONFIG.BASE_URL}/auth/refresh`,
      {
        refresh_token: currentSession.refresh_token,
        // Pass current access_token so the backend can decode user identity
        // without consuming an extra refresh token (even if it's expired).
        access_token: currentSession.access_token,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": import.meta.env.VITE_API_KEY,
        },
      }
    );

    // Update Supabase's local session with the new tokens from the backend.
    // This triggers onAuthStateChange(TOKEN_REFRESHED) in AuthProvider.
    await supabase.auth.setSession({
      access_token: refreshData.access_token,
      refresh_token: refreshData.refresh_token,
    });

    return refreshData.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// Add JWT token interceptor for Supabase authentication
api.interceptors.request.use(
  async (config) => {
    const accessToken = await getAccessToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 — Tekion-expired 401s require a backend refresh to renew tekion_verified_at.
// The refresh lock (ensureFreshSession) ensures only one refresh fires even if multiple
// requests get 401 simultaneously.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await ensureFreshSession();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch {
        await handleSessionExpired();
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 403) {
      console.error("Access forbidden:", error.response.data);
    }

    return Promise.reject(error);
  }
);

export default api;
